import { Feature } from '@23degrees/shared/models/pricing/features.model';
import { Product } from '@23degrees/shared/models/pricing/products.model';

import { PERMISSIONS } from '@23degrees/shared-code/config/permissions';
import { ChartTypes, MapTypes, NoDataTypes, TTLineChartTypes } from '@23degrees/shared-code/config/constants';

export interface ContentAccess {
  allowedByOwner: boolean;
  deniedByOwner: boolean;
  permission: boolean;
  isOwner: boolean;
  granted: boolean;
  grantedFree: boolean;
  isPrivate?: boolean;
  isPermissionOwner: boolean;
  isPermissionOwnerAndContentOwner: boolean;
  isSharedWithMe: boolean;
  isDatasetSharedWithMe: boolean;
}

export interface ContentFeature {
  type: string;
  label: string;
  url: string;
  icon: string;
  listedIn: string[];
  reuseType: string;
  permissionId: string;
  feature: Feature;
  requiredProducts: Product[];
  notAvailableByContentType: string[];
  hideIfChild: boolean;
  access: ContentAccess;
  notAvailableOnChanges: boolean;
  ownerOnly?: boolean;
  showOnPermission?: string;
}

export const PERMISSION_MAPPING = {
  image: PERMISSIONS.CONTENT_DOWNLOAD_PNG,
  vector: PERMISSIONS.CONTENT_DOWNLOAD_SVG,
  data: PERMISSIONS.CONTENT_DOWNLOAD_XLS,
  bulkpdf: PERMISSIONS.CONTENT_BULKPDF,
  embed: PERMISSIONS.EMBED,
  api: PERMISSIONS.API_GET,
  geojson: PERMISSIONS.CONTENT_DOWNLOAD_GEOJSON,
};

export const CONTENT_FEATURES: ContentFeature[] = [
  {
    type: 'image',
    label: 'export.features.image.label',
    url: 'image',
    icon: 'picture',
    listedIn: ['exportButtons'],
    reuseType: 'exportPNG',
    permissionId: undefined,
    feature: undefined,
    requiredProducts: [],
    hideIfChild: false,
    notAvailableOnChanges: false,
    notAvailableByContentType: ['dataset', 'report', 'atlas'],
    access: {
      allowedByOwner: false,
      deniedByOwner: false,
      permission: false,
      isOwner: false,
      granted: false,
      grantedFree: false,
      isPermissionOwner: false,
      isPermissionOwnerAndContentOwner: false,
      isSharedWithMe: false,
      isDatasetSharedWithMe: false,
    },
  },
  {
    type: 'data',
    label: 'export.features.data.label',
    url: 'data',
    icon: 'database',
    listedIn: ['exportButtons'],
    reuseType: 'exportXLS',
    permissionId: undefined,
    feature: undefined,
    requiredProducts: [],
    hideIfChild: false,
    notAvailableOnChanges: false,
    notAvailableByContentType: NoDataTypes,
    access: {
      allowedByOwner: false,
      deniedByOwner: false,
      permission: false,
      isOwner: false,
      isPrivate: true,
      granted: false,
      grantedFree: false,
      isPermissionOwner: false,
      isPermissionOwnerAndContentOwner: false,
      isSharedWithMe: false,
      isDatasetSharedWithMe: false,
    },
  },
  {
    type: 'vector',
    label: 'export.features.vector.label',
    url: 'vector',
    icon: 'vector',
    listedIn: ['exportButtons'],
    reuseType: 'exportSVG',
    permissionId: undefined,
    feature: undefined,
    requiredProducts: [],
    hideIfChild: false,
    notAvailableOnChanges: false,
    notAvailableByContentType: ['dataset', 'report', 'atlas'],
    access: {
      allowedByOwner: false,
      deniedByOwner: false,
      permission: false,
      isOwner: false,
      granted: false,
      grantedFree: false,
      isPermissionOwner: false,
      isPermissionOwnerAndContentOwner: false,
      isSharedWithMe: false,
      isDatasetSharedWithMe: false,
    },
  },
  {
    type: 'api',
    label: 'export.features.api.label',
    url: 'api',
    icon: 'plug',
    listedIn: ['exportButtons'],
    reuseType: 'exportXLS',
    permissionId: undefined,
    feature: undefined,
    hideIfChild: false,
    requiredProducts: [],
    notAvailableOnChanges: true,
    notAvailableByContentType: NoDataTypes,
    access: {
      allowedByOwner: false,
      deniedByOwner: false,
      permission: false,
      isOwner: false,
      isPrivate: true,
      granted: false,
      grantedFree: false,
      isPermissionOwner: false,
      isPermissionOwnerAndContentOwner: false,
      isSharedWithMe: false,
      isDatasetSharedWithMe: false,
    },
  },
  {
    type: 'embed',
    label: 'export.features.embed.label',
    url: 'embed',
    icon: 'code',
    listedIn: ['exportButtons'],
    reuseType: 'embed',
    permissionId: undefined,
    feature: undefined,
    requiredProducts: [],
    hideIfChild: false,
    notAvailableOnChanges: true,
    notAvailableByContentType: ['dataset'],
    access: {
      allowedByOwner: false,
      deniedByOwner: false,
      permission: false,
      isOwner: false,
      granted: false,
      grantedFree: false,
      isPermissionOwner: false,
      isPermissionOwnerAndContentOwner: false,
      isSharedWithMe: false,
      isDatasetSharedWithMe: false,
    },
  },
  {
    type: 'geojson',
    label: 'export.features.geojson.label',
    url: 'vector',
    icon: 'json',
    listedIn: [],
    reuseType: undefined,
    permissionId: undefined,
    feature: undefined,
    requiredProducts: [],
    hideIfChild: false,
    notAvailableOnChanges: false,
    notAvailableByContentType: ['dataset', 'report', 'atlas', 'multimap', ...ChartTypes, ...TTLineChartTypes],
    access: {
      allowedByOwner: false,
      deniedByOwner: false,
      permission: false,
      isOwner: false,
      granted: false,
      grantedFree: false,
      isPermissionOwner: false,
      isPermissionOwnerAndContentOwner: false,
      isSharedWithMe: false,
      isDatasetSharedWithMe: false,
    },
  },
  {
    type: 'bulkpdf',
    label: 'export.features.bulkpdf.label',
    url: 'bulkpdf',
    icon: 'pdf',
    listedIn: ['exportButtons'],
    reuseType: undefined,
    permissionId: undefined,
    feature: undefined,
    requiredProducts: [],
    hideIfChild: false,
    notAvailableOnChanges: false,
    notAvailableByContentType: ['dataset', 'table', 'atlas', ...ChartTypes, ...MapTypes, ...TTLineChartTypes],
    access: {
      allowedByOwner: false,
      deniedByOwner: false,
      permission: false,
      isOwner: false,
      granted: false,
      grantedFree: false,
      isPermissionOwner: false,
      isPermissionOwnerAndContentOwner: false,
      isSharedWithMe: false,
      isDatasetSharedWithMe: false,
    },
    ownerOnly: true,
    showOnPermission: PERMISSIONS.CONTENT_BULKPDF_VIEW,
  },
];
